import { useTranslation } from '../i18n/useTranslation';
import type { AccountBalance } from '../types/gateway';

interface AccountBalancePanelProps {
  balance: AccountBalance | null;
  className?: string;
}

function formatUsd(n: number): string {
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function pnlColor(n: number): string {
  if (n > 0) return 'text-success';
  if (n < 0) return 'text-danger';
  return 'text-foreground';
}

export function AccountBalancePanel({ balance, className = '' }: AccountBalancePanelProps) {
  const { t } = useTranslation();

  return (
    <div className={`bg-card rounded border border-border p-2 ${className}`}>
      <div className="drag-handle h-5 flex items-center cursor-grab active:cursor-grabbing mb-1">
        <span className="panel-header">{t('account.header' as Parameters<typeof t>[0])}</span>
      </div>
      {!balance ? (
        <div className="flex items-center justify-center h-[calc(100%-24px)]">
          <span className="text-[9px] font-mono text-muted animate-pulse">
            {t('common.awaiting')} {t('account.label')}...
          </span>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[9px] font-mono h-[calc(100%-24px)] content-center">
          <div>
            <div className="text-muted-foreground uppercase">{t('account.wallet')}</div>
            <div className="text-foreground text-[11px]">{formatUsd(balance.wallet_balance)}</div>
          </div>
          <div>
            <div className="text-muted-foreground uppercase">{t('account.margin')}</div>
            <div className="text-foreground text-[11px]">{formatUsd(balance.margin_balance)}</div>
          </div>
          <div>
            <div className="text-muted-foreground uppercase">{t('account.available')}</div>
            <div className="text-foreground text-[11px]">{formatUsd(balance.available_balance)}</div>
          </div>
          <div>
            <div className="text-muted-foreground uppercase">{t('account.unrealized_pnl')}</div>
            <div className={`text-[11px] ${pnlColor(balance.unrealized_pnl)}`}>
              {balance.unrealized_pnl >= 0 ? '+' : ''}{formatUsd(balance.unrealized_pnl)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
